import { Divider, RadioGroup, RadioGroupItem, Typography } from '@musinsa/mds';
import { Fragment, type ReactNode } from 'react';
import type { SeedingContentType } from '../types';
import { CampaignInfo } from './CampaignInfo';

interface ContentTypeOption {
  type: SeedingContentType;
  label: string;
  /** 업로드 기한 안내 텍스트 (예: "배송 완료일로부터 7일 이내 업로드") */
  deadline?: string;
}

interface ContentTypeRadioProps {
  mode: 'radio';
  options: ContentTypeOption[];
  selectedType: SeedingContentType | null;
  onSelect: (type: SeedingContentType) => void;
  /**
   * 라디오 직후 같은 섹션의 형제 아이템 (예: 브랜드 메시지 입력).
   * content 와 함께 gap-12 그룹으로 묶여 렌더 → Container 의 gap-y-16 은 Title↔그룹에만 적용되고,
   * 라디오↔footer 간격은 그룹 gap-12 + footer 컴포넌트 자체 pt-12 = 24px.
   */
  footer?: ReactNode;
}

interface ContentTypeInfoRow {
  label: string;
  value: string;
}

interface ContentTypeInfoProps {
  mode: 'info';
  rows: ContentTypeInfoRow[];
}

interface ContentTypeFrozenDetailProps {
  mode: 'frozen-detail';
  snsChannel: string;
  contentTypeLabel: string;
  uploadDeadlineNotice: string;
  /** 큐레이터가 작성한 브랜드 메시지 (선택) */
  brandMessage?: string;
  /** 메시지 영역 타이틀 (default = "어떤 콘텐츠를 제작하실 예정인가요?") */
  brandMessageTitle?: string;
}

type ContentTypeSectionProps =
  | ContentTypeRadioProps
  | ContentTypeInfoProps
  | ContentTypeFrozenDetailProps;

/** 콘텐츠 유형 섹션 — 타이틀/외곽 여백은 CampaignInfo(Container pt-12·gap-y-16·px-16) 표준 사용. */
export const ContentTypeSection = (props: ContentTypeSectionProps) => (
  <CampaignInfo.Container>
    <CampaignInfo.Title>콘텐츠 유형</CampaignInfo.Title>
    {/* content(라디오/frozen) + (라디오 한정)브랜드 메시지 를 한 그룹으로 묶는다. 그룹 gap-12 +
        브랜드메시지 컴포넌트 자체 pt-12 = 라디오↔브랜드메시지 24px. Title↔그룹은 Container gap-y-16(16px).
        frozen/info 는 단일 자식이라 gap 무영향. */}
    <div className="flex flex-col gap-[12px]!">
      {props.mode === 'radio' && (
        <RadioContent
          options={props.options}
          selectedType={props.selectedType}
          onSelect={props.onSelect}
        />
      )}
      {props.mode === 'info' && <InfoContent rows={props.rows} />}
      {props.mode === 'frozen-detail' && (
        <FrozenDetailContent
          snsChannel={props.snsChannel}
          contentTypeLabel={props.contentTypeLabel}
          uploadDeadlineNotice={props.uploadDeadlineNotice}
          brandMessage={props.brandMessage}
          brandMessageTitle={props.brandMessageTitle ?? '어떤 콘텐츠를 제작하실 예정인가요?'}
        />
      )}
      {props.mode === 'radio' && props.footer}
    </div>
  </CampaignInfo.Container>
);

const FrozenDetailContent = ({
  snsChannel,
  contentTypeLabel,
  uploadDeadlineNotice,
  brandMessage,
  brandMessageTitle,
}: {
  snsChannel: string;
  contentTypeLabel: string;
  uploadDeadlineNotice: string;
  brandMessage?: string;
  brandMessageTitle: string;
}) => (
  // SNS/콘텐츠유형 행 묶음 ↔ 브랜드 메시지 = 24px (radio 와 동일). 행간은 내부 gap-8.
  // 하단 12px 은 Container pb-12 가 담당. brandMessage 없으면 행 묶음 단일 자식 → outer gap 무영향.
  <div className="flex flex-col gap-[24px]!">
    <div className="flex flex-col gap-[8px]!">
      <div className="flex justify-between items-center">
        {/* 라벨/값 모두 black 고정 — 폰트 컬러 black 통일. (SID-4150) */}
        <Typography variant="body13pxReg" colorScheme="black">
          SNS 채널
        </Typography>
        <Typography variant="body13pxReg" colorScheme="black">
          {snsChannel}
        </Typography>
      </div>
      <div className="flex justify-between items-center">
        <Typography variant="body13pxReg" colorScheme="black">
          {contentTypeLabel}
        </Typography>
        <Typography variant="body13pxReg" colorScheme="black">
          {uploadDeadlineNotice}
        </Typography>
      </div>
    </div>
    {brandMessage && (
      // 브랜드 메시지 sub-block — 위 행 묶음과 24px(outer gap), 내부 title↔body 4px.
      <div className="flex flex-col gap-[4px]!">
        <Typography variant="body14pxMed" colorScheme="black">
          {brandMessageTitle}
        </Typography>
        <Typography
          variant="body13pxReg"
          colorScheme="gray-600"
          // 사용자가 textarea 에 입력한 값을 그대로 노출. whitespace-pre-wrap = 개행(\n) 보존,
          // break-words = 공백 없는 긴 문자열도 UI 밖으로 새지 않도록 강제 줄바꿈. (QAT-39546)
          className="whitespace-pre-wrap break-words"
        >
          {brandMessage}
        </Typography>
      </div>
    )}
  </div>
);

const RadioContent = ({
  options,
  selectedType,
  onSelect,
}: {
  options: ContentTypeOption[];
  selectedType: SeedingContentType | null;
  onSelect: (type: SeedingContentType) => void;
}) => (
  // 행 간 16px(gap-y). 타이틀↔첫 행은 Container gap-y-16=16px, 라디오↔footer 는 상위 그룹 gap-8.
  <RadioGroup className="flex flex-col gap-y-[16px]!">
    {options.map((option) => {
      const isSelected = selectedType === option.type;
      return (
        <div
          key={option.type}
          role="button"
          tabIndex={0}
          className="flex items-center justify-between cursor-pointer"
          onClick={() => onSelect(option.type)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              onSelect(option.type);
            }
          }}
        >
          <div className="flex items-center gap-[8px]">
            <RadioGroupItem value={option.type} checked={isSelected} />
            <Typography variant="body13pxReg" colorScheme={isSelected ? 'black' : 'gray-600'}>
              {option.label}
            </Typography>
          </div>
          {option.deadline && isSelected && (
            <Typography variant="body13pxReg" colorScheme="gray-600">
              {option.deadline}
            </Typography>
          )}
        </div>
      );
    })}
  </RadioGroup>
);

const InfoContent = ({ rows }: { rows: ContentTypeInfoRow[] }) => (
  // frozen 공통: row gap-8 / 하단 pb-12. divider 는 gap 직접 자식으로 두어 상하 8px 대칭.
  <div className="flex flex-col gap-[8px]! pb-[12px]!">
    {rows.map((row, index) => (
      <Fragment key={row.label}>
        {index > 0 && <Divider variant="line" />}
        <div className="flex justify-between items-center">
          {/* label/value 모두 black — 폰트 컬러 black 통일. (SID-4150) */}
          <Typography variant="body13pxReg" colorScheme="black">
            {row.label}
          </Typography>
          <Typography variant="body13pxReg" colorScheme="black">
            {row.value}
          </Typography>
        </div>
      </Fragment>
    ))}
  </div>
);
